import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Sparkles, Megaphone } from "lucide-react";

const TABS = [
  { id: "landing", label: "Landing page" },
  { id: "leads", label: "Lead section" },
  { id: "settings", label: "Settings" },
  { id: "ai", label: "AI Automation" },
];

function Field({ label, value, onChange, area }) {
  return (
    <div className="space-y-1">
      <Label className="text-slate-300">{label}</Label>
      {area ? (
        <Textarea className="bg-slate-950 border-slate-800 text-slate-100 min-h-[88px]" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input className="bg-slate-950 border-slate-800 text-slate-100" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export default function SuperGads() {
  const [tab, setTab] = useState("landing");
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ai, setAi] = useState({ enabled: false, auto_apply: false, interval_hours: 6 });
  const [leads, setLeads] = useState([]);
  const [logs, setLogs] = useState([]);
  const [heat, setHeat] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [clarityTesting, setClarityTesting] = useState(false);

  const loadPage = async () => {
    const { data } = await api.get("/platform/gads/page");
    setContent(data.content);
    setSettings(data.settings);
    setAi(data.ai || ai);
  };
  const loadLeads = async () => {
    const { data } = await api.get("/platform/gads/leads", { params: { q } });
    setLeads(data.items || []);
  };
  const loadAi = async () => {
    const { data } = await api.get("/platform/gads/ai");
    setAi(data.ai || ai);
    setLogs(data.logs || []);
  };
  const loadHeat = async () => {
    const { data } = await api.get("/platform/gads/heatmap");
    setHeat(data);
  };

  useEffect(() => { loadPage().catch((e) => toast.error(extractErr(e))); }, []);
  useEffect(() => {
    if (tab === "leads") loadLeads().catch((e) => toast.error(extractErr(e)));
    if (tab === "ai") { loadAi().catch(() => {}); loadHeat().catch(() => {}); }
  }, [tab]);

  const saveContent = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/gads/content", content);
      setContent(data.content);
      toast.success("Landing page saved");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };
  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/gads/settings", settings);
      setSettings(data.settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };
  const testClarity = async () => {
    setClarityTesting(true);
    try {
      const { data } = await api.post("/platform/gads/clarity-test");
      if (data?.ok) {
        toast.success(`Clarity connected · ${data.metrics?.length || 0} metric groups`);
        loadHeat().catch(() => {});
      } else {
        toast.error(data?.error || "Clarity data is unavailable");
      }
    } catch (e) { toast.error(extractErr(e)); } finally { setClarityTesting(false); }
  };
  const saveAi = async (extra = {}) => {
    setRunning(!!extra.run_now);
    try {
      const { data } = await api.put("/platform/gads/ai", { ...ai, ...extra });
      setAi(data.ai);
      if (data.run) setLogs((prev) => [data.run, ...prev]);
      toast.success(extra.run_now ? "AI run logged" : "AI settings saved");
      if (extra.run_now) loadAi().catch(() => {});
    } catch (e) { toast.error(extractErr(e)); } finally { setRunning(false); }
  };

  const setHero = (k, v) => setContent({ ...content, hero: { ...content.hero, [k]: v } });
  const setForm = (k, v) => setContent({ ...content, form: { ...content.form, [k]: v } });
  const setMeta = (k, v) => setContent({ ...content, meta: { ...content.meta, [k]: v } });

  if (!content || !settings) {
    return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading Google Ads hub…</div>;
  }

  const webhook = typeof window !== "undefined" ? `${window.location.origin}/api/gads/google-lead-webhook` : "/api/gads/google-lead-webhook";
  const live = "/india-shopify-alternative";

  return (
    <div className="p-6 space-y-5" data-testid="super-gads">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-white font-display font-bold text-xl">
            <Megaphone className="w-5 h-5 text-emerald-300" /> Google Ads
          </div>
          <p className="text-sm text-slate-400 mt-1">Landing, leads, conversion tags, heatmap and AI. Live URL <a className="text-emerald-300 underline" href={live} target="_blank" rel="noreferrer">{live}</a></p>
        </div>
        <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-500/30">Google Lead tag · round-robin</Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} type="button" data-testid={`gads-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-3 py-1.5 rounded-lg text-sm ${tab === t.id ? "bg-emerald-500/20 text-emerald-100 border border-emerald-400/30" : "bg-white/5 text-slate-400"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "landing" && (
        <div className="space-y-4">
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-4">
            <div className="text-slate-200 font-semibold">Meta & hero</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Page title" value={content.meta?.title} onChange={(v) => setMeta("title", v)} />
              <Field label="Hero badge" value={content.hero?.badge} onChange={(v) => setHero("badge", v)} />
              <div className="md:col-span-2"><Field label="H1" value={content.hero?.h1} onChange={(v) => setHero("h1", v)} /></div>
              <div className="md:col-span-2"><Field area label="Subheadline" value={content.hero?.sub} onChange={(v) => setHero("sub", v)} /></div>
              <Field label="Primary CTA" value={content.hero?.primary_cta} onChange={(v) => setHero("primary_cta", v)} />
              <Field label="See demo URL" value={content.hero?.demo_url} onChange={(v) => setHero("demo_url", v)} />
              <Field label="Buy URL" value={content.hero?.buy_url} onChange={(v) => setHero("buy_url", v)} />
              <Field label="Buy CTA" value={content.hero?.buy_cta} onChange={(v) => setHero("buy_cta", v)} />
            </div>
          </Card>
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-3">
            <div className="text-slate-200 font-semibold">Form copy</div>
            <Field label="Form title" value={content.form?.title} onChange={(v) => setForm("title", v)} />
            <Field area label="Form subtitle" value={content.form?.subtitle} onChange={(v) => setForm("subtitle", v)} />
            <Field label="Submit label" value={content.form?.submit_label} onChange={(v) => setForm("submit_label", v)} />
            <Field label="Popup title" value={content.form?.popup_title} onChange={(v) => setForm("popup_title", v)} />
            <Field area label="Popup subtitle" value={content.form?.popup_subtitle} onChange={(v) => setForm("popup_subtitle", v)} />
          </Card>
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-2">
            <div className="text-slate-200 font-semibold">Advanced JSON</div>
            <p className="text-xs text-slate-500">Edit any section (FAQ, compare table, testimonials, pricing). Invalid JSON will not save.</p>
            <Textarea className="bg-slate-950 border-slate-800 text-slate-100 font-mono text-xs min-h-[280px]" value={JSON.stringify(content, null, 2)} onChange={(e) => {
              try { setContent(JSON.parse(e.target.value)); } catch { /* keep typing */ }
            }} />
          </Card>
          <Button onClick={saveContent} disabled={saving} data-testid="gads-save-landing"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save landing page"}</Button>
        </div>
      )}

      {tab === "leads" && (
        <Card className="p-5 bg-[#0f1623] border-slate-800">
          <div className="flex gap-2 mb-4">
            <Input className="bg-slate-950 border-slate-800 text-slate-100" placeholder="Search name, phone, email, tag" value={q} onChange={(e) => setQ(e.target.value)} />
            <Button variant="outline" onClick={loadLeads}>Search</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-200">
              <thead className="text-slate-500 text-left">
                <tr>{["Name", "Contact", "Category", "Start", "Tag", "When"].map((h) => <th key={h} className="pb-2 pr-3 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {leads.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">No Google Ads leads yet.</td></tr>}
                {leads.map((l) => {
                  const f = l.fields || {};
                  return (
                    <tr key={l.id} className="border-t border-slate-800" data-testid={`gads-lead-${l.id}`}>
                      <td className="py-2 pr-3">{l.name || "—"}</td>
                      <td className="py-2 pr-3 text-xs">{l.phone}<br />{l.email}</td>
                      <td className="py-2 pr-3">{f.business_category || "—"}</td>
                      <td className="py-2 pr-3">{f.when_to_start || "—"}</td>
                      <td className="py-2 pr-3 text-emerald-300 text-xs">{l.tag}</td>
                      <td className="py-2 pr-3 text-xs text-slate-500">{l.created_at ? new Date(l.created_at).toLocaleString("en-IN") : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "settings" && (
        <div className="space-y-4">
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-3">
            <div className="text-slate-200 font-semibold">Google tags (required for Ads lead conversion)</div>
            <p className="text-xs text-slate-500">In Google Ads: Tools → Conversions → New conversion action → Website → lead / submit lead form. Paste the AW- id and conversion label. The landing page fires <code className="text-emerald-300">gtag conversion</code> + <code className="text-emerald-300">generate_lead</code> on every successful form submit. For Lead form assets, set the webhook below.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Google Tag Manager (GTM-XXXX)" value={settings.gtm_id} onChange={(v) => setSettings({ ...settings, gtm_id: v })} />
              <Field label="GA4 (G-XXXX)" value={settings.ga4_id} onChange={(v) => setSettings({ ...settings, ga4_id: v })} />
              <Field label="Google Ads ID (AW-XXXX)" value={settings.google_ads_id} onChange={(v) => setSettings({ ...settings, google_ads_id: v })} />
              <Field label="Conversion label" value={settings.google_ads_conversion_label} onChange={(v) => setSettings({ ...settings, google_ads_conversion_label: v })} />
              <Field label="Conversion value (INR)" value={String(settings.conversion_value || "")} onChange={(v) => setSettings({ ...settings, conversion_value: v })} />
              <Field label="Lead form webhook key" value={settings.google_ads_lead_form_key} onChange={(v) => setSettings({ ...settings, google_ads_lead_form_key: v })} />
            </div>
            <div className="text-xs text-slate-400 break-all">Google Ads lead form webhook: <span className="text-emerald-300">{webhook}</span></div>
          </Card>
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-3">
            <div className="text-slate-200 font-semibold">Heatmap & extra pixels</div>
            <div className="flex items-center gap-3">
              <Switch checked={!!settings.heatmap_enabled} onCheckedChange={(v) => setSettings({ ...settings, heatmap_enabled: v })} />
              <span className="text-sm text-slate-300">Store click / scroll heatmap on this landing page (used by AI)</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Microsoft Clarity project ID" value={settings.clarity_id} onChange={(v) => setSettings({ ...settings, clarity_id: v })} />
              <div className="space-y-1 md:col-span-2">
                <Label className="text-slate-300">Clarity Data Export API token</Label>
                <Textarea
                  className="bg-slate-950 border-slate-800 text-slate-100 min-h-[88px] font-mono text-xs"
                  value={settings.clarity_api_token || ""}
                  onChange={(e) => setSettings({ ...settings, clarity_api_token: e.target.value })}
                  placeholder={settings.clarity_api_token_set ? "Token saved — paste the full JWT to replace" : "Paste the full JWT from Clarity → Settings → Data Export"}
                  data-testid="gads-clarity-token"
                />
                <p className="text-[11px] text-slate-500">
                  Project admin token from Clarity → Settings → Data Export. Paste the entire JWT (usually 500+ characters), not the token name.
                  {settings.clarity_api_token_set ? ` Saved length: ${settings.clarity_api_token_len || "?"} chars.` : ""}
                  {settings.clarity_api_token_looks_short ? " That length is too short for a Clarity JWT — re-paste the full token." : ""}
                  {" "}Leave blank when saving to keep the stored token. Recordings and visual heatmaps stay in Clarity (not in the API).
                </p>
              </div>
              <Field label="Hotjar site ID" value={settings.hotjar_id} onChange={(v) => setSettings({ ...settings, hotjar_id: v })} />
              <Field label="Hotjar SV" value={settings.hotjar_sv} onChange={(v) => setSettings({ ...settings, hotjar_sv: v })} />
              <Field label="Meta Pixel ID" value={settings.fb_pixel_id} onChange={(v) => setSettings({ ...settings, fb_pixel_id: v })} />
            </div>
          </Card>
          <Button onClick={saveSettings} disabled={saving} data-testid="gads-save-settings"><Save className="w-4 h-4 mr-2" />Save settings</Button>
          <Button type="button" variant="outline" className="ml-2 border-slate-700 text-slate-200" disabled={clarityTesting} onClick={testClarity} data-testid="gads-clarity-test">
            {clarityTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Test Clarity connection
          </Button>
        </div>
      )}

      {tab === "ai" && (
        <div className="space-y-4">
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-slate-200 font-semibold">AI automation</div>
                <p className="text-xs text-slate-500">Uses OpenRouter from Integrations. Feeds landing copy, first-party click/scroll counts, Clarity Data Export metrics when a token is saved (not recordings), and Google Ads leads.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">On</span>
                <Switch checked={!!ai?.enabled} onCheckedChange={(v) => saveAi({ enabled: v })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!ai?.auto_apply} onCheckedChange={(v) => saveAi({ auto_apply: v })} />
              <span className="text-sm text-slate-300">Auto-apply suggested copy to the live landing page</span>
            </div>
            <Button disabled={running} onClick={() => saveAi({ run_now: true })} data-testid="gads-ai-run"><Sparkles className="w-4 h-4 mr-2" />{running ? "Running…" : "Run AI now"}</Button>
            {heat && (
              <div className="text-xs text-slate-400 space-y-1">
                <div>First-party heatmap last {heat.days}d: {heat.clicks} clicks, {heat.scrolls} scrolls, mean scroll depth {heat.avg_scroll_depth}% (not section reach)</div>
                <div>
                  {heat.clarity_token_configured
                    ? (heat.clarity_api?.ok
                      ? `Clarity export: ${heat.clarity_api.metrics?.length || 0} metric groups${heat.clarity_fetched_at ? ` · fetched ${new Date(heat.clarity_fetched_at).toLocaleString("en-IN")}` : ""}`
                      : (heat.clarity_api?.error || heat.clarity_last_error || "Clarity data is unavailable until the Data Export token is tested successfully."))
                    : (heat.clarity_configured
                      ? "Clarity script is on the landing page. Add a Data Export API token in Settings for AI to read Clarity analytics."
                      : "Clarity is not configured")}
                </div>
              </div>
            )}
          </Card>
          <Card className="p-5 bg-[#0f1623] border-slate-800 space-y-3">
            <div className="text-slate-200 font-semibold">Log</div>
            {(logs || []).length === 0 && <p className="text-slate-500 text-sm">No runs yet.</p>}
            {(logs || []).map((row) => (
              <div key={row.id} className="border border-slate-800 rounded-lg p-3 text-sm text-slate-300 space-y-1">
                <div className="flex gap-2 text-xs text-slate-500">
                  <span>{row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : ""}</span>
                  <span>{row.trigger}</span>
                  <span>{row.status}</span>
                  {row.applied ? <Badge className="bg-emerald-500/20 text-emerald-200">applied</Badge> : null}
                </div>
                <div>{row.summary}</div>
                {row.first_party_mongo ? <div className="text-xs text-slate-400"><span className="text-slate-500">First-party Mongo · </span>{row.first_party_mongo}</div> : null}
                {row.clarity_api ? <div className="text-xs text-slate-400"><span className="text-slate-500">Clarity API · </span>{row.clarity_api}</div> : null}
                {row.google_ads ? <div className="text-xs text-slate-400"><span className="text-slate-500">Google Ads · </span>{row.google_ads}</div> : null}
                {(row.recommendations || []).map((x) => <div key={x} className="text-emerald-200/90">• {x}</div>)}
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
